// src/services/barcodeService.ts - Version finale pour votre projet

import { 
    collection, 
    query, 
    where, 
    getDocs, 
    orderBy, 
    limit, 
  } from 'firebase/firestore';
  import { db } from '../config/firebase';
  import { COLLECTIONS } from '../config/collections';
  import { 
    validateBarcode, 
    generateInternalCode, 
    extractSequenceFromInternalCode,
    BarcodeValidationResult,
    BarcodeFormat 
  } from '../utils/barcodeUtils';
  
  export interface BarcodeCheckResult {
    isAvailable: boolean;
    existingProductId?: string;
    existingProductName?: string;
  }
  
  export interface GeneratedBarcodeResult {
    barcode: string;
    format: BarcodeFormat;
    sequence: number;
  }
  
  // Vérifier la disponibilité d'un code-barres
  export const checkBarcodeAvailability = async (
    barcode: string, 
    businessId: string,
    excludeProductId?: string
  ): Promise<BarcodeCheckResult> => {
    if (!barcode || !businessId) {
      return { isAvailable: false };
    }
  
    try {
      const q = query(
        collection(db, COLLECTIONS.products),
        where('businessId', '==', businessId),
        where('barcode', '==', barcode.trim()),
        where('isActive', '==', true)
      );
  
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return { isAvailable: true };
      }
  
      const existingDoc = snapshot.docs[0];
      if (excludeProductId && existingDoc.id === excludeProductId) {
        return { isAvailable: true };
      }
  
      const existingProduct = existingDoc.data();
      return {
        isAvailable: false,
        existingProductId: existingDoc.id,
        existingProductName: existingProduct.name
      };
  
    } catch (error) {
      console.error('Erreur lors de la vérification du code-barres:', error);
      return { isAvailable: false };
    }
  };
  
  // Obtenir le prochain numéro de séquence
  export const getNextInternalSequence = async (
    businessId: string,
    businessType: string
  ): Promise<number> => {
    try {
      const prefixes = {
        'beauty_salon': 'BEA-',
        'bakery': 'BAK-',
        'electromenager': 'ELE-',
        'restaurant': 'RES-',
        'retail_store': 'RET-',
        'garage': 'GAR-',
        'prosthesis_clinic': 'PRO-',
        'custom': 'CUS-'
      };
  
      const prefix = prefixes[businessType as keyof typeof prefixes] || 'GEN-';
  
      const q = query(
        collection(db, COLLECTIONS.products),
        where('businessId', '==', businessId),
        where('barcode', '>=', prefix),
        where('barcode', '<', prefix + '\uf8ff'),
        orderBy('barcode', 'desc'),
        limit(1)
      );
  
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return 1;
      }
  
      const lastProduct = snapshot.docs[0].data();
      const lastBarcode = lastProduct.barcode || '';
      const lastSequence = extractSequenceFromInternalCode(lastBarcode);
      
      return lastSequence + 1;
  
    } catch (error) {
      console.error('Erreur lors de la récupération de la séquence:', error);
      return 1;
    }
  };
  
  // Générer un code-barres interne unique
  export const generateUniqueInternalBarcode = async (
    businessId: string,
    businessType: string,
    categoryName?: string
  ): Promise<GeneratedBarcodeResult> => {
    try {
      const categoryPrefix = categoryName 
        ? categoryName.substring(0, 3).replace(/[^A-Za-z]/g, '').toUpperCase()
        : undefined;
  
      const sequence = await getNextInternalSequence(businessId, businessType);
      const barcode = generateInternalCode(businessType, sequence, categoryPrefix);
      
      const availability = await checkBarcodeAvailability(barcode, businessId);
      
      if (!availability.isAvailable) {
        const fallbackSequence = sequence + Math.floor(Math.random() * 100) + 1;
        const fallbackBarcode = generateInternalCode(businessType, fallbackSequence, categoryPrefix);
        
        return {
          barcode: fallbackBarcode,
          format: BarcodeFormat.INTERNAL,
          sequence: fallbackSequence
        };
      }
  
      return {
        barcode,
        format: BarcodeFormat.INTERNAL,
        sequence
      };
  
    } catch (error) {
      console.error('Erreur lors de la génération du code-barres:', error);
      
      const timestamp = Date.now().toString().slice(-6);
      const fallbackBarcode = generateInternalCode(businessType, parseInt(timestamp));
      
      return {
        barcode: fallbackBarcode,
        format: BarcodeFormat.INTERNAL,
        sequence: parseInt(timestamp)
      };
    }
  };
  
  // Rechercher un produit par code-barres
  export const findProductByBarcode = async (
    barcode: string,
    businessId: string
  ): Promise<any | null> => {
    if (!barcode || !businessId) return null;
  
    try {
      const q = query(
        collection(db, COLLECTIONS.products),
        where('businessId', '==', businessId),
        where('barcode', '==', barcode.trim()),
        where('isActive', '==', true)
      );
  
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) return null;
  
      const doc = snapshot.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        businessId: data.businessId,
        name: data.name,
        categoryId: data.categoryId,
        categoryName: data.categoryName,
        barcode: data.barcode,
        sellingPrice: data.sellingPrice || undefined,
        costPrice: data.costPrice || undefined,
        minStockAlert: data.minStockAlert,
        currentStock: data.currentStock,
        description: data.description,
        brand: data.brand,
        isActive: data.isActive,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      };
  
    } catch (error) {
      console.error('Erreur lors de la recherche par code-barres:', error);
      return null;
    }
  };
  
  // Valider et nettoyer un code-barres
  export const validateAndCleanBarcode = async (
    barcode: string,
    businessId: string,
    businessType: string,
    excludeProductId?: string
  ): Promise<{
    isValid: boolean;
    cleanBarcode: string;
    validation: BarcodeValidationResult;
    availability: BarcodeCheckResult;
  }> => {
    const validation = validateBarcode(barcode);
    
    if (!validation.isValid) {
      return {
        isValid: false,
        cleanBarcode: validation.cleanCode,
        validation,
        availability: { isAvailable: false }
      };
    }
  
    const availability = await checkBarcodeAvailability(
      validation.cleanCode, 
      businessId, 
      excludeProductId
    );
  
    return {
      isValid: validation.isValid && availability.isAvailable,
      cleanBarcode: validation.cleanCode,
      validation,
      availability
    };
  };
  
  // Suggérer des codes-barres
  export const suggestBarcodes = async (
    businessId: string,
    businessType: string,
    productName: string,
    categoryName?: string
  ): Promise<string[]> => {
    const suggestions: string[] = [];
  
    try {
      if (categoryName) {
        const categoryResult = await generateUniqueInternalBarcode(
          businessId, 
          businessType, 
          categoryName
        );
        suggestions.push(categoryResult.barcode);
      }
  
      const genericResult = await generateUniqueInternalBarcode(
        businessId, 
        businessType
      );
      suggestions.push(genericResult.barcode);
  
      const productPrefix = productName
        .substring(0, 3)
        .replace(/[^A-Za-z]/g, '')
        .toUpperCase()
        .padEnd(3, 'X');
      
      const sequence = await getNextInternalSequence(businessId, businessType);
      const productBasedCode = `${productPrefix}-${sequence.toString().padStart(6, '0')}`;
      suggestions.push(productBasedCode);
  
      // Supprimer les doublons manuellement pour compatibilité TS
      const uniqueSuggestions: string[] = [];
      suggestions.forEach(suggestion => {
        if (!uniqueSuggestions.includes(suggestion)) {
          uniqueSuggestions.push(suggestion);
        }
      });
      return uniqueSuggestions;
  
    } catch (error) {
      console.error('Erreur lors de la suggestion de codes-barres:', error);
      return [];
    }
  };